import React from 'react'
import {transform} from 'sucrase'
import {logger, Util} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {Sync} from '../client/sync'
import {ShowMessageBox} from '../types/message-box'
import {Api} from './api'
import {Context} from './context'
import {ContextObject} from './context-object'

export type CompileScript = (script: string) => string
type ContextObjectDict = Record<string, typeof ContextObject>

export interface ContextObjectStore {
  objects: ContextObjectDict
  extra: Record<string, unknown>
}

const disabled = [
  'self',
  'window',
  'document',
  'localStorage',
  'sessionStorage'
]

export class Runner {
  private static contextObjectStore: ContextObjectStore
  private static showMessage: ShowMessageBox
  private static compileScript: CompileScript

  static init(
    contextObjectStore: ContextObjectStore,
    showMessage: ShowMessageBox,
    compileScript: CompileScript
  ): void {
    Runner.contextObjectStore = contextObjectStore
    Runner.showMessage = showMessage
    Runner.compileScript = compileScript
  }

  static runJs(
    script: string,
    name: string,
    companyId: DataObjectId,
    extensionId: string | undefined
  ): void {
    const {contextObjectStore} = Runner
    if (!contextObjectStore) {
      logger.error('Should call Runner.init before Runner.runJs!')
      return
    }

    const {extra, objects} = contextObjectStore

    if (!script) {
      return
    }

    try {
      const emptyObjects = disabled.map(Util.noop)
      const box = Sync.get({companyId})
      const context = new Context(box, name, extensionId)
      const objInstances: {[k: string]: ContextObject} = {}
      const global = {}
      const codeWithInclusions = script.replace(
        /include *\( *['"]([^'"]+)['"] *\)/g,
        (_: string, scrName: string): string => {
          const s = Api.findScript(box, scrName)
          if (!s) {
            throw new Error('Script not found: ' + scrName)
          }
          return '\n' + s.description + '\n'
        }
      )

      const shouldCompileJsx = !script.startsWith('/**!precompiled-jsx*/')

      const {code} = shouldCompileJsx
        ? transform(codeWithInclusions, {
            transforms: ['jsx'],
            jsxPragma: 'React.createElement',
            jsxFragmentPragma: 'React.Fragment'
          })
        : {code: codeWithInclusions}

      script = Runner.compileScript(code)

      Object.keys(objects).forEach((key: string) => {
        const obj = objects[key]

        if (obj) {
          const inst = new obj(context, global)
          objInstances[key] = inst
          obj.register(inst)
        }
      })
      const func = new Function(
        ...Object.keys(extra),
        ...Object.keys(objects),
        ...Object.keys(global),
        ...disabled,
        'React',
        '"use strict"; ' + script
      )
      func.call(
        global,
        ...Object.keys(extra).map((k: string) => extra[k]),
        ...Object.keys(objInstances).map((k: string) => objInstances[k]),
        ...Object.keys(global).map((k: string) => (global as any)[k]),
        ...emptyObjects,
        React
      )
    } catch (e) {
      const stub = 'open console for more details'
      const message = `Error in script "${name}":\n${stub}`

      Runner.showMessage(message, 'danger')
      logger.error(e)
    }
  }

  static unloadAll(): void {
    const {contextObjectStore} = Runner
    if (!contextObjectStore) {
      logger.error('Should call Runner.init before Runner.runJs!')
      return
    }

    const {objects} = contextObjectStore

    if (!objects) {
      logger.error('Should call Runner.init before Runner.unloadJs!')
      return
    }

    Object.keys(objects).forEach((key: string) => {
      const obj = objects[key]

      if (obj) {
        obj.unregisterAll()
      }
    })
  }

  static unloadJs(name: string, companyId: DataObjectId): void {
    const {contextObjectStore} = Runner
    if (!contextObjectStore) {
      logger.error('Should call Runner.init before Runner.runJs!')
      return
    }

    const {objects} = contextObjectStore

    if (!objects) {
      logger.error('Should call Runner.init before Runner.unloadJs!')
      return
    }

    Object.keys(objects).forEach((key: string) => {
      const obj = objects[key]

      if (obj) {
        obj.unregister(companyId, name)
      }
    })
  }
}
